import type {
  InputDefinition,
  OutputDefinition,
  UniversalNodeSchema,
} from './universal-node-schema'

/**
 * Whether an input is in play for a node's current settings: the one rule the
 * node face, the readiness validator (graph-engine/graph-validator.ts) and the
 * pre-run check (utils/validate-graph.ts) all read, so they cannot disagree.
 *
 * Three declarations on an input:
 *   - `activeWhen` (and `activeWhenAll`, for more settings at once): shown and
 *     required only while a setting has one of the listed values (a Target's
 *     signal, only for Condition or Static Value).
 *   - `requiredWhen`: always shown, required only while a setting has one of
 *     the listed values (Price Data's universe socket).
 *   - `retired`: no longer offered; drawn only while a saved wire holds it.
 *
 * A wired input is always shown, active or not: 28 of the 46 cookbook recipes
 * wire their trigger into a Market Entry Rules, and a hidden socket would leave
 * that wire pointing at nothing. See
 * docs/superpowers/specs/2026-10-04-backtest-node-rendering-audit.md.
 */

/** A setting's value from either stored shape: a record, or a list of `{ name, value }`. */
export function readParameter(parameters: unknown, name: string): unknown {
  if (Array.isArray(parameters)) {
    const hit = parameters.find(
      (p): p is { name: string; value?: unknown } =>
        !!p && typeof p === 'object' && 'name' in p && p.name === name,
    )
    return hit?.value
  }
  if (parameters && typeof parameters === 'object')
    return (parameters as Record<string, unknown>)[name]
  return undefined
}

function settingMatches(
  schema: UniversalNodeSchema,
  rule: { parameter: string; is: ReadonlyArray<string | number | boolean> },
  parameters: unknown,
): boolean {
  const value =
    readParameter(parameters, rule.parameter) ??
    schema.parameters.find((p) => p.name === rule.parameter)?.default
  return rule.is.some((allowed) => allowed === value)
}

/** Every setting rule an input's activity reads: `activeWhen`, then `activeWhenAll`. */
function activityRules(input: InputDefinition) {
  return [...(input.activeWhen ? [input.activeWhen] : []), ...(input.activeWhenAll ?? [])]
}

/** In play for these settings: no rule, or every rule's setting matches. */
export function isInputActive(
  schema: UniversalNodeSchema,
  input: InputDefinition,
  parameters: unknown,
): boolean {
  return activityRules(input).every((rule) => settingMatches(schema, rule, parameters))
}

/**
 * A socket no longer offered (`retired`): drawn only while a saved wire holds
 * it, and refused a new wire, but in play, so that saved wire is kept and run.
 */
export function isRetiredSocket(input: InputDefinition | undefined): boolean {
  return input?.retired !== undefined
}

/**
 * A socket whose wire does nothing: the node's settings leave it out of play,
 * and the node reaches the engine through a lowering that reads only the
 * sockets in play (the Trade Manager: utils/lower-trade-managers.ts, held to it
 * by its test). Such a wire is refused, let go on load, and never drawn, so a
 * mode's section shows its one socket (Chris, 2026-10-06: Multi Target showed
 * a price level and an exit condition beside its own). A node the engine reads
 * as is keeps its wire (a Market Entry Rules' trigger, which the cookbook
 * relies on).
 */
export function isDeadSocket(
  schema: UniversalNodeSchema,
  inputId: string,
  parameters: unknown,
): boolean {
  if (!schema.compound?.lower) return false
  const input = schema.inputs.find((candidate) => candidate.id === inputId)
  return input !== undefined && !isInputActive(schema, input, parameters)
}

/**
 * Why a socket is dead, for the AI that asked to wire it: the setting and the
 * values that read it, as the graph ops spell them.
 */
export function deadSocketReason(schema: UniversalNodeSchema, inputId: string): string {
  const input = schema.inputs.find((candidate) => candidate.id === inputId)
  const rules = input ? activityRules(input) : []
  if (rules.length === 0) return `"${schema.type}" does not read "${inputId}" with these settings.`
  const clauses = rules.map(
    // As the graph ops spell values: "Condition", true.
    (rule) => `${rule.parameter} is ${rule.is.map((value) => JSON.stringify(value)).join(' or ')}`,
  )
  const named = rules.length === 1 ? rules[0].parameter : 'them'
  return `"${schema.type}" reads "${inputId}" only while ${clauses.join(' and ')}; set ${named} first.`
}

/** Why a new wire into a retired socket is refused: where the same thing is set now. */
export function retiredSocketReason(schema: UniversalNodeSchema, inputId: string): string {
  const input = schema.inputs.find((candidate) => candidate.id === inputId)
  return `"${schema.type}" no longer takes a wire into "${inputId}": ${input?.retired ?? ''}`.trim()
}

/**
 * An output drawn on the node face: any but a retired one (the Condition's
 * False), which shows only while a saved wire holds it.
 */
export function isOutputShown(output: OutputDefinition, wired: boolean): boolean {
  return output.retired === undefined || wired
}

/** The outputs offered for a new wire, in the AI's catalog and help: all but the retired. */
export function offeredOutputs(schema: UniversalNodeSchema): OutputDefinition[] {
  return schema.outputs.filter((output) => output.retired === undefined)
}

/** Drawn on the node face: active, or something is wired into it. */
export function isInputShown(
  schema: UniversalNodeSchema,
  input: InputDefinition,
  parameters: unknown,
  wired: boolean,
): boolean {
  if (isRetiredSocket(input)) return wired
  return wired || isInputActive(schema, input, parameters)
}

/**
 * The settings-dependent part of "must this input be wired": not optional,
 * active, and its `requiredWhen` (if any) met. Callers still apply the input
 * handler's count rules (a single-input node requires only its first input).
 */
export function isInputRequiredNow(
  schema: UniversalNodeSchema,
  input: InputDefinition,
  parameters: unknown,
): boolean {
  if (input.optional) return false
  if (!isInputActive(schema, input, parameters)) return false
  return !input.requiredWhen || settingMatches(schema, input.requiredWhen, parameters)
}

/**
 * The sockets a setting change takes out of play: keyed to that setting
 * (`activeWhen` or `activeWhenAll`) and inactive under its new value, the
 * node's other settings (`parameters`, either stored shape) read as they are.
 * A face that opts in (`face.releaseInactive`) drops their wires on the change.
 */
export function inputsReleasedBy(
  schema: UniversalNodeSchema,
  parameter: string,
  value: unknown,
  parameters?: unknown,
): string[] {
  const after: Record<string, unknown> = {}
  for (const def of schema.parameters) after[def.name] = readParameter(parameters, def.name)
  after[parameter] = value
  return schema.inputs
    .filter((input) => isKeyedTo(input, parameter) && !isInputActive(schema, input, after))
    .map((input) => input.id)
}

/** Whether a setting is one an input's activity reads. */
export function isKeyedTo(input: InputDefinition, parameter: string): boolean {
  return activityRules(input).some((rule) => rule.parameter === parameter)
}
